import React, { FC } from 'react'
import { TimezoneOverrideContainer } from './style'

//* utils
import { ModuleNames } from '../../../constants/componentNames.constants'
import ImgUtil from '../../../utils/img.utils'
import { findTarget } from '../../../utils/microcopy.utils'
import { renderRichText } from '../../../utils/richTextHandler.utils'
import formatInlineCode from '../../../utils/shortTextFormatting'

//* components
import ContentBlock from '../../global/contentBlock'
import FeaturedResource from '../../global/featuredResource'
import Hero from '../../global/hero'
import DynamicRow from '../../global/relatedResources'
import { ResourceRowContainer } from '../../global/relatedResources/style'
import ResourceRow from '../../global/resourceRow'
import SEOTags from '../../global/seoTags'
import Typography from '../../global/typography'
import WrapperImage from '../../global/wrapperImage'

type Props = {
  data: any
  thankYouPageHero: any
  thankYouFeaturedResource: any
  locale: string
  relatedResources: any
  availableLocales: any
  path: string
  location: any
}

export const Modules: FC<Props> = ({
  data,
  thankYouPageHero,
  thankYouFeaturedResource,
  locale,
  relatedResources,
  availableLocales,
  path,
  location,
}) => {
  let displayDynamicRow: boolean = true
  const resourceNumber = data.number || 8
  const generics = data.genericIntro?.values || []
  const isUpcoming = data.referrer?.useCase === 'Upcoming'

  const resourceRowIntro =
    data.resourceIntro || findTarget(`resource.${resourceNumber}.ThankYouROWINTRO.text`, generics)

  const thankYouPageContent = data.flexSection || data.modules || []
  const dynamicPageContent = thankYouPageContent.map((module: any, i: number) => {
    switch (module.__typename) {
      case ModuleNames.GLOBAL_CONTENT_BLOCK:
        return <ContentBlock data={module} key={`page-component--${i}`} />

      case ModuleNames.RESOURCE_ROW:
        //* disable the dynamic row if the module is present
        displayDynamicRow = false

        return (
          <ResourceRowContainer key={`page-component-container--${i}`}>
            <Typography className="ResourseRow_Intro" type="header2">
              {formatInlineCode(
                resourceRowIntro ||
                  findTarget(`resource.${resourceNumber}.ThankYouROWINTRO.text`, generics)
              )}
            </Typography>

            <ResourceRow
              data={module}
              pageType={'thankYou'}
              key={`page-component--${i}`}
              locale={locale}
            />
          </ResourceRowContainer>
        )

      case ModuleNames.FEATURED_RESOURCE:
        return (
          <FeaturedResource
            data={module}
            pageLocale={locale}
            pageType={'thankYou'}
            key={`page-component--${i}`}
          />
        )
    }
  })

  const timeZoneImgAltText = data.timezoneOverride?.icon?.colorIcon?.seo
    ? data.timezoneOverride.icon.colorIcon.seo
    : 'UiPath Icon'

  return (
    <div data-cy={`ThankYouPage`}>
      <SEOTags
        seoMetadata={data.seoMetadata}
        slug={data.slug || ''}
        path={path}
        locale={locale}
        siteSection="Product"
        availableLocales={availableLocales}
      />

      {isUpcoming && data.timezoneOverride ? (
        <TimezoneOverrideContainer>
          {data.timezoneOverride?.icon?.colorIcon?.file?.url && (
            <WrapperImage
              src={ImgUtil.getResizedImageUrl(data.timezoneOverride.icon.colorIcon.file.url, '')}
              alt={timeZoneImgAltText}
              loading="lazy"
              className="TimeZoneOverried__Icon"
              threshold={150}
            />
          )}

          {data.timezoneOverride.body && renderRichText(data.timezoneOverride.body)}
        </TimezoneOverrideContainer>
      ) : (
        <>
          <Hero
            data={thankYouPageHero}
            location={location}
            pageTitle={data.name}
            showBreadcrumbs={true}
            pageType="thankYou"
            locale={locale}
          />

          {dynamicPageContent}

          {displayDynamicRow && (
            <DynamicRow
              resourceRowIntro={resourceRowIntro}
              featuredResource={thankYouFeaturedResource || null}
              relatedResources={relatedResources}
              location={location}
              locale={locale}
            />
          )}
        </>
      )}
    </div>
  )
}
